<template>
  <div class="pricing-card-simple" :class="[{ featured: plan.isFeatured }, cardClass]">
    <div v-if="plan.isFeatured" class="pricing-badge">{{ plan.badgeText }}</div>
    
    <div class="pricing-header">
      <h4 class="text-h6 font-weight-bold">
        <v-icon 
          v-if="plan.icon" 
          color="var(--color-pallet-1)" 
          class="mr-2"
        >{{ plan.icon }}</v-icon>
        <svg 
          v-else-if="shouldShowCustomIcon()" 
          class="asport-people-icon mr-2" 
          width="24" 
          height="24" 
          viewBox="0 0 211 191" 
          xmlns="http://www.w3.org/2000/svg"
        >
          <path fill="currentColor" d="M32.011635,159.169052 C43.710545,153.851089 55.360649,149.071381 66.824043,143.879456 C72.409103,141.349915 77.782768,138.642960 81.619263,133.104538 C86.503891,126.052986 92.451759,119.718513 98.149490,113.258575 C100.674187,110.396141 101.769119,107.997948 99.691872,104.167427 C97.563301,100.242256 96.476067,95.752388 94.809944,91.171204 C94.723740,91.198814 93.746185,91.312370 92.991180,91.782967 C74.195168,103.498451 55.427105,115.258759 36.641785,126.991425 C33.356163,129.043503 30.381138,128.531479 28.007673,125.496246 C25.456242,122.233414 28.567772,120.838966 30.358915,119.151268 C39.708988,110.341171 49.004303,101.469185 58.506504,92.825218 C71.775635,80.754578 86.612366,71.120277 103.565979,64.993385 C121.224632,58.611698 138.015182,50.487980 154.311890,41.139645 C167.406311,33.628254 181.040741,27.056015 194.463715,20.121574 C197.029007,18.796318 199.986404,17.623081 201.960159,20.742651 C203.824722,23.689655 202.714035,25.768303 199.720352,28.025696 C185.724640,38.579250 172.040466,49.545410 158.218231,60.329853 C152.445892,64.833572 146.278214,68.900497 140.956451,73.878227 C138.750732,75.941353 136.932175,80.039253 137.290695,82.918816 C138.362518,91.527245 140.619019,99.986427 142.372421,108.512215 C144.096161,116.893578 151.310791,117.683411 157.673126,120.037270 C165.260605,122.844398 172.919510,125.754257 179.943176,129.696320 C185.604126,132.873550 187.052383,139.325104 187.973526,145.474457 C189.465775,155.436478 190.782059,165.425232 192.322449,175.379517 C193.159348,180.787628 191.059662,186.038757 187.391586,186.924026 C184.038055,187.733398 179.891068,184.658401 178.009537,179.798767 C174.841415,171.616180 171.402863,163.513901 168.746124,155.165237 C166.515488,148.155533 161.356735,146.367615 155.064255,145.068054 C146.429367,143.284714 137.675949,141.537552 129.448792,138.490936 C123.075317,136.130737 118.085335,137.193085 112.914734,140.649399 C104.626373,146.189804 96.631516,152.281509 87.902725,157.015717 C80.769775,160.884384 72.816261,163.311752 65.096428,166.005188 C58.306370,168.374222 51.336067,170.226746 44.445110,172.306503 C36.126968,174.817017 27.875847,177.611420 19.435677,179.605606 C17.665714,180.023819 14.035986,177.975266 13.310766,176.218277 C12.036216,173.130402 12.238112,169.040863 15.792036,167.096786 C20.982021,164.257782 26.504959,162.027420 32.011635,159.169052 z M127.039337,25.328485 C131.342209,42.204796 124.746086,52.141235 109.672188,55.639847 C100.797569,57.699619 87.861084,50.607635 86.090347,38.590076 C84.156158,25.463238 91.360359,16.079800 103.961266,13.772232 C114.823067,11.783145 121.040375,17.612595 127.039337,25.328485 z"/>
        </svg>
        {{ plan.title }}
      </h4>
      <div class="pricing-price">
        <span class="price-amount">{{ plan.price }}</span>
        <span v-if="plan.period" class="price-period">{{ plan.period }}</span>
      </div>
    </div>
    
    <!-- Who it is for (once), the plan's limits, and one line that every feature is included.
         The same card on the home page and both pricing pages, so they cannot say different things. -->
    <div class="plan-description mt-4 mb-6">
      <p v-if="plan.description" class="text-body-2 text-color-pallet-2 plan-for-whom">
        {{ plan.description }}
      </p>
      <dl v-if="plan.limits" class="plan-limits">
        <div v-for="limit in plan.limits" :key="limit.key" class="plan-limits__row">
          <dt>{{ limit.label }}</dt>
          <dd>{{ limit.value }}</dd>
        </div>
      </dl>
      <router-link v-if="plan.detailsUrl" :to="plan.detailsUrl" class="plan-details-link">
        {{ plan.detailsLabel }} →
      </router-link>
      <router-link :to="featuresUrl" class="plan-all-features">
        <v-icon small color="var(--color-pallet-1)" class="mr-2">mdi-check-circle</v-icon>
        {{ includesEverything ? $t('pages.pricing.all-features-included') : $t('pages.pricing.basic-features') }}
      </router-link>
    </div>
    
    <!-- CTA Button -->
    <v-btn
      color="var(--color-pallet-1)"
      :outlined="!plan.isFeatured"
      :class="['pricing-btn', { 'featured-btn': plan.isFeatured }]"
      @click="handleCTA"
    >
      {{ plan.ctaText }}
    </v-btn>
  </div>
</template>

<script>
import { PRICING_PLANS } from '@/data/pricingPlans'
import urlUtil from '@/js/utils/urlUtil'
import { MainPath } from '@/router/mainPaths'

export default {
  name: 'PricingCard',
  props: {
    plan: {
      type: Object,
      required: true
    },
    link: {
      type: String,
      default: ''
    },
    cardClass: {
      type: String,
      default: ''
    }
  },
  computed: {
    isLoggedIn() {
      return this.$store.getters['user/isLoggedIn']
    },
    /** False for a plan that leaves feature areas out (Starter): its card must not say "everything". */
    includesEverything() {
      return !(this.plan.excludedFeatureAreas && this.plan.excludedFeatureAreas.length)
    },
    featuresUrl() {
      return urlUtil.getLocalisedUrl('/' + MainPath.FEATURES)
    }
  },
  methods: {
    shouldShowCustomIcon() {
      return this.plan.title === 'Pro'
    },
    handleCTA() {
      // A plan that asks to talk first (the studio card on the home page) carries its own link.
      if (this.plan.ctaHref) {
        window.location.href = this.plan.ctaHref
        return
      }
      // Check if this is an enterprise plan (custom pricing)
      if (this.plan.id === PRICING_PLANS.organization.enterprise) {
        // Open email client for enterprise pricing inquiries
        const subject = encodeURIComponent(this.$t('pages.pricing.cta.email.subject-enterprise'))
        const body = encodeURIComponent(this.$t('pages.pricing.cta.email.body-enterprise'))
        window.location.href = `mailto:info@asport.lv?subject=${subject}&body=${body}`
        return
      }
      
      // When a plan is selected, everyone (logged-in or guest) goes to public new-organization page
      if (this.plan?.id) {
        const planQuery = '?plan=' + encodeURIComponent(this.plan.id)
        this.$router.push(urlUtil.getLocalisedUrl('/new-organization') + planQuery)
      } else if (this.link) {
        this.$router.push(this.link)
      } else {
        this.$router.push(urlUtil.getLocalisedUrl('/signup'))
      }
    }
  }
}
</script>

<style scoped>
.pricing-card-simple {
  background: white;
  border-radius: 16px;
  padding: 2rem 1.5rem;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
  height: 100%;
  border: 2px solid transparent;
  position: relative;
  min-height: 380px;
  display: flex;
  flex-direction: column;
}

.pricing-card-simple:hover {
  box-shadow: 0 12px 40px rgba(46, 139, 87, 0.25);
  border-color: var(--color-pallet-1);
  background: linear-gradient(135deg, rgba(46, 139, 87, 0.02) 0%, rgba(46, 139, 87, 0.05) 100%);
}

.pricing-card-simple.featured {
  border-color: var(--color-pallet-1);
  box-shadow: 0 6px 30px rgba(46, 139, 87, 0.15);
  transform: scale(1.02);
}

.pricing-card-simple.featured:hover {
  box-shadow: 0 15px 50px rgba(46, 139, 87, 0.35);
  border-color: var(--color-pallet-2);
  background: linear-gradient(135deg, rgba(46, 139, 87, 0.05) 0%, rgba(46, 139, 87, 0.08) 100%);
}

.pricing-card-simple:hover .pricing-header h4 .v-icon,
.pricing-card-simple:hover .pricing-header h4 .asport-people-icon {
  transform: none;
  color: var(--color-pallet-2) !important;
  filter: drop-shadow(0 2px 4px rgba(46, 139, 87, 0.3));
}

.pricing-card-simple:hover .pricing-header h4 {
  color: var(--color-pallet-1) !important;
}

.pricing-card-simple:hover .price-amount {
  color: var(--color-pallet-2) !important;
  text-shadow: 0 1px 2px rgba(46, 139, 87, 0.2);
}

.pricing-badge {
  position: absolute;
  top: -12px;
  left: 50%;
  transform: translateX(-50%);
  background: linear-gradient(135deg, var(--color-pallet-1) 0%, var(--color-pallet-2) 100%);
  color: white;
  padding: 8px 20px;
  border-radius: 20px;
  font-size: 0.8rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.5px;
  box-shadow: 0 4px 12px rgba(46, 139, 87, 0.3);
  width: 200px;
}

.pricing-header {
  text-align: center;
  margin-bottom: 1.5rem;
  flex-shrink: 0;
  padding-bottom: 1rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.1);
}

.pricing-header h4 {
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 0;
}

.pricing-header h4 .v-icon {
  transition: all 0.3s ease;
}

.pricing-card-simple.featured .pricing-header h4 .v-icon,
.pricing-card-simple.featured .pricing-header h4 .asport-people-icon {
  filter: drop-shadow(0 2px 4px rgba(46, 139, 87, 0.3));
}

.pricing-price {
  margin-top: 0.75rem;
}

.price-amount {
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-pallet-1);
}

.price-period {
  font-size: 0.95rem;
  color: var(--color-grey);
  margin-left: 0.5rem;
}

.plan-description {
  /*flex-grow: 1;
  display: flex;
  flex-direction: column;*/
  justify-content: center;
  text-align: center;
  padding: 1rem 0;
}

.plan-for-whom {
  line-height: 1.5;
  margin-bottom: 16px;
}

.plan-limits {
  text-align: left;
  margin-bottom: 16px;
}

.plan-limits__row {
  display: flex;
  justify-content: space-between;
  gap: 12px;
  padding: 8px 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.06);
  font-size: 0.9rem;
}

.plan-limits__row dt {
  color: var(--color-pallet-2);
  min-width: 0;
}

.plan-limits__row dd {
  font-weight: 700;
  color: var(--color-pallet-5);
  white-space: nowrap;
}

.plan-details-link {
  display: block;
  margin-bottom: 12px;
  font-weight: 600;
  color: var(--color-pallet-1) !important;
  text-decoration: underline;
}

/* A soft pill, not an underlined link: it is a promise ("everything is included") that also opens /features. */
.plan-all-features {
  display: inline-flex;
  align-items: center;
  min-height: 36px;
  padding: 6px 14px;
  border-radius: 999px;
  background: rgba(46, 139, 87, 0.1);
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--color-pallet-1) !important;
  text-decoration: none;
  transition: background 0.2s ease;
}

.plan-all-features:hover,
.plan-all-features:focus-visible {
  background: rgba(46, 139, 87, 0.18);
}

.asport-people-icon {
  color: var(--color-pallet-1);
}

/* Pricing button styles */
.pricing-card-simple .pricing-btn {
  text-transform: none !important;
  font-weight: 600 !important;
  border-radius: 28px !important;
  padding: 14px 28px !important;
  font-size: 1.05rem !important;
  margin-top: auto !important;
  transition: all 0.3s ease !important;
  border-width: 2px !important;
  width: 90% !important;
  min-width: 200px !important;
  max-width: 260px !important;
  align-self: center !important;
  white-space: normal;
  height: auto;
  display: block;
}

.pricing-card-simple.pricing-btn:hover {
  box-shadow: 0 8px 25px rgba(46, 139, 87, 0.4) !important;
  background-color: var(--color-pallet-2) !important;
  border-color: var(--color-pallet-2) !important;
}

.pricing-card-simple .featured-btn {
  background: linear-gradient(135deg, var(--color-pallet-1) 0%, var(--color-pallet-2) 100%) !important;
  color: white !important;
  border: none !important;
}

.pricing-card-simple .featured-btn:hover {
  background: linear-gradient(135deg, var(--color-pallet-2) 0%, var(--color-pallet-1) 100%) !important;
  box-shadow: 0 8px 25px rgba(46, 139, 87, 0.5) !important;
}

/* Organization card variant styling */
.pricing-card-simple.organization-card-variant {
  background: linear-gradient(135deg, rgba(46, 139, 87, 0.04) 0%, rgba(46, 139, 87, 0.08) 100%);
  border: 2px solid var(--color-pallet-1);
  border-style: dashed;
}

.pricing-card-simple.organization-card-variant:hover {
  background: linear-gradient(135deg, rgba(46, 139, 87, 0.08) 0%, rgba(46, 139, 87, 0.12) 100%);
  border-style: solid;
}

/* Responsive improvements */
@media (max-width: 768px) {
  .pricing-card-simple {
    min-height: 450px;
    padding: 1.5rem 1rem;
  }
  
  .pricing-card-simple.featured {
    transform: none;
  }
  
  .pricing-card-simple.featured:hover {
    transform: translateY(-6px);
  }
}

@media (max-width: 600px) {
  .pricing-card-simple {
    min-height: auto;
  }
}
</style> 